import { Route, Routes } from "react-router-dom";
import Authentication from "./modules/authentication";
import AppBar from "./components/AppBar";
import SideMenu from "./components/SideMenu";
import ProcessStatusDrawer from "./components/ProcessStatusDrawer";
import Footer from "./components/Footer";
import PageHeading from "./components/PageHeading";
import { routes } from "./routes";
import { useSelector } from "react-redux";
import { useLoadAllData } from "./hooks/useLoadAllData";
import Loading from "./components/Loading";
import { useCheckForLoginAndRedirect } from "./hooks/useCheckForLoginAndRedirect";
import { useProcessStatuses } from "./hooks/useProcessStatuses";
import { useLocalStorageState } from "./hooks/useLocalStorageState";
import { useSessionStorageState } from "./hooks/useSessionStorageState";
import { useEffect, useState } from "react";
import { getWebsiteStage, WEBSITE_URLS_BY_STAGE } from "./modules/stages";
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Toolbar,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

/** An interception switch - intercept each time URL is changed, and checks if user is logged-in -
 * if not, redirect to login screen. */
function InterceptSwitch({ routes }) {
    // This checks if location changed, and if user is not logged in and needs to be redirected to login page
    useCheckForLoginAndRedirect();

    return (
        <Routes>
            {routes.map((route) => (
                <Route
                    key={route.path}
                    path={route.path}
                    element={route.element}
                />
            ))}
        </Routes>
    );
}

function App() {
    const loginState = useSelector((state) => state.user.loginState);
    const userDetails = useSelector((state) => state.user.userDetails);
    const userStage = userDetails ? userDetails.stage : null;
    const websiteStage = getWebsiteStage();
    const loadingData = useLoadAllData();
    const [showRedirectDialog, setShowRedirectDialog] = useState(false);
    const [redirecting, setRedirecting] = useState(false);
    const [sideMenuOpen, setSideMenuOpen] = useLocalStorageState(
        "iris.sideMenuOpen",
        true,
    );
    const [processDrawerOpen, setProcessDrawerOpen] = useSessionStorageState(
        "iris.processDrawerOpen",
        true,
    );
    const loggedIn = loginState === Authentication.LOGIN_STATUS_LOGGED_IN;

    // Keep process statuses up to date, regardless of whether the process statuses drawer is open
    useProcessStatuses();

    useEffect(() => {
        if (!userStage) return;

        if (userStage !== websiteStage) {
            // User stage is not the same as current website stage - show a dialog letting the user know
            // that we're going to redirect him to the correct website URL.
            setShowRedirectDialog(true);
        }
    }, [userStage]);

    const redirectToStagingWebsite = async () => {
        setRedirecting(true);

        // Log-off from this website
        Authentication.logOut();

        setTimeout(() => {
            // Redirect user to correct staging website
            window.location.replace(
                `${window.location.protocol}//${WEBSITE_URLS_BY_STAGE[userStage][0]}`,
            );
        }, 500);
    };

    return (
        <Box sx={{ display: "flex", height: "100vh" }}>
            <Dialog
                open={showRedirectDialog}
                onClose={() => {
                    // Prevent from canceling the dialog by clicking on the backdrop
                    return;
                }}
                maxWidth={"sm"}
                fullWidth={true}
            >
                <DialogTitle>Wrong Staging Website</DialogTitle>
                <DialogContent>
                    <div>
                        <b>User</b>: {userDetails && userDetails.email}
                    </div>
                    <div>
                        <b>User stage</b>: {userStage}
                    </div>
                    <div>
                        <b>Website stage</b>: {websiteStage}
                    </div>
                </DialogContent>
                <DialogActions>
                    <LoadingButton
                        onClick={redirectToStagingWebsite}
                        loading={redirecting}
                    >
                        Redirect me to the correct website
                    </LoadingButton>
                </DialogActions>
            </Dialog>
            <AppBar
                onToggleSideMenu={() => setSideMenuOpen(!sideMenuOpen)}
                onToggleProcessDrawer={() =>
                    setProcessDrawerOpen(!processDrawerOpen)
                }
            />
            {loggedIn && (
                <SideMenu
                    open={sideMenuOpen}
                    onOpen={() => setSideMenuOpen(true)}
                />
            )}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Toolbar />
                {loggedIn && <PageHeading />}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "auto",
                        // Logged-out pages (e.g. login) are centered, as there is no side menu
                        ...(!loggedIn && { alignItems: "center", pt: 5 }),
                    }}
                >
                    {loadingData && !showRedirectDialog ? (
                        <div className="flex flex-row justify-center pt-10">
                            <Loading />
                        </div>
                    ) : (
                        <InterceptSwitch routes={routes} />
                    )}
                </Box>
                <Footer />
            </Box>
            {loggedIn && <ProcessStatusDrawer open={processDrawerOpen} />}
        </Box>
    );
}

export default App;
